import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * A button.
 *
 * `label` is an i18n key, not text — a lint rule forbids bare JSX strings, and
 * this is where that rule stops being an annoyance and starts being the reason
 * the German build has no English left in it.
 *
 * The primary is `brand`, never `cue-green`: green means "the beat being
 * played" everywhere a teacher looks in this product, and a Save button is not
 * a beat.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand text-white hover:bg-brand-hover disabled:bg-brand/40",
  secondary: "border border-line bg-white text-ink hover:bg-surface-muted disabled:text-ink-faint",
  ghost: "bg-transparent text-ink-soft hover:bg-surface-muted hover:text-ink disabled:text-ink-faint",
  danger: "bg-danger text-white hover:bg-danger/90 disabled:bg-danger/40",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shown while a request is in flight; also disables the button. */
  busy?: boolean;
  children: ReactNode;
}

export function Button({
  variant = "primary",
  busy = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-control
                  px-4 py-2 text-[14px] font-medium leading-5 transition-colors
                  disabled:cursor-not-allowed
                  ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
